<template>
  <div>
    <menu-pages-navbar class="mb-3" />
    <div class="mt-4">
      <!-- Loading Indicator -->
      <div class="loading-indicator">
        <v-progress-circular
          v-if="loading"
          id="content-loading-indicator"
          :size="50"
          indeterminate
          class="mt-5 msr--text"
        />
      </div>
      <v-slide-x-transition>
        <div v-if="!loading">
          <v-layout
            align-center
            wrap
          >
            <!-- Search -->
            <v-text-field
              v-model="searchText"
              class="mt-3 mr-4"
              :label="$t('search')"
              :hint="searchHint"
              persistent-hint
              clearable
              append-icon="search"
              style="max-width: 300px"
              @click:clear="clearSearch()"
              @keyup.native.esc="clearSearch()"
            />
            <!-- Filter for players without categories only -->
            <v-slide-x-transition>
              <v-checkbox
                v-if="hasDecodersWithoutAllowedMenuCategories"
                v-model="onlyDecodersWithoutMenuCategories"
                :label="$t('menuCategoryDecodersFilterOnlyWithoutMenuCategory')"
                hide-details
                color="warning"
                :class="{'warning--text': onlyDecodersWithoutMenuCategories}"
              />
            </v-slide-x-transition>
            <!-- Categories that are not assigned to any players yet -->
            <v-slide-x-reverse-transition>
              <div
                v-if="menuCategoriesNotUsedOnAnyPlayersYet.length > 0"
                class="warning--text px-3 ml-auto"
                :class="{'my-3': $vuetify.breakpoint.xs}"
              >
                <span>{{
                  $t('menuCategoriesNotUsedOnAnyDecodersYet')
                }}:&nbsp;</span>
                <v-layout wrap>
                  <span
                    v-for="(
                      menuCategory, menuCategoryIndex
                    ) in menuCategoriesNotUsedOnAnyPlayersYet"
                    :key="menuCategory.id"
                  >
                    <span class="font-weight-bold">{{ menuCategory.name }}</span><span
                      v-if="
                        menuCategoryIndex <
                          menuCategoriesNotUsedOnAnyPlayersYet.length - 1
                      "
                    >,&nbsp;</span>
                  </span>
                </v-layout>
              </div>
            </v-slide-x-reverse-transition>

            <!-- StoreMenu Link -->
            <v-btn
              :disabled="filteredStoresWithTVDecoders.length == 0"
              class="subheading mb-2"
              :class="{'ml-auto': $vuetify.breakpoint.smAndUp}"
              color="grey darken-3"
              round
              flat
              :title="$t('menuCategoryDecodersGoToStoreMenu')"
              :to="menuCategoryDecodersAssignmentRoute"
            >
              <v-icon left>
                shortcut
              </v-icon>
              {{ $t('menuCategoryDecodersGoToStoreMenu') }}
            </v-btn>
          </v-layout>
          <!-- Stores -->
          <transition-group name="stores-transition">
            <div
              v-for="store in filteredStoresWithTVDecoders"
              :key="'store-' + store.id"
              class="mt-4 mb-2 white"
            >
              <div
                class="unselectable pa-2 clickable store-menu-anchor--row"
                style="display: flex; flex-flow: row wrap; align-items: center"
                :title="$t('menuItemAssignStoresDialogGoToStoreTooltip')"
                @click.stop="onGoToStorePageLinkClicked(store)"
              >
                <v-icon
                  class="ml-0 mr-2"
                  color="msr"
                  medium
                >
                  location_on
                </v-icon>
                <div class="store-menu-anchor">
                  <span
                    class="grey--text text--darken-3"
                    style="font-size: 0.9em"
                  >
                    {{ store.name }}
                  </span>
                  <div>
                    <strong>
                      {{ store.city }}
                    </strong>
                    <span v-if="store.location">{{ store.location }}</span>
                  </div>
                  <div>
                    {{ store.address }}
                  </div>
                </div>
                <div
                  v-if="$vuetify.breakpoint.mdAndUp"
                  class="
                    store-menu-link--hint
                    mx-auto
                    grey--text
                    text--darken-1
                    subheading
                  "
                >
                  {{ $t('menuItemAssignStoresDialogGoToStoreTooltip') }}
                  <v-icon class="ml-2">
                    chevron_right
                  </v-icon>
                </div>
              </div>
              <!-- TVDecoders -->
              <div class="mt-2 unselectable">
                <transition-group name="stores-transition">
                  <v-layout
                    v-for="tvdecoder in store.tvdecoders"
                    :key="store.id + tvdecoder.ident"
                    align-center
                    class="py-2 pr-2 decoder-row"
                    style="padding-left: 32px"
                    wrap
                  >
                    <v-flex
                      xs12
                      sm4
                    >
                      <v-layout align-center>
                        <icon-tv-decoder
                          :tvdecoder="tvdecoder"
                          class="mr-3"
                        />
                        <div
                          class="pseudo-anchor--inverted"
                          :title="$t('goToDecoderDetailPage')"
                          @click.stop="goToDecoderDetailPage(tvdecoder)"
                        >
                          {{ tvdecoder.description }}
                        </div>
                      </v-layout>
                    </v-flex>
                    <v-flex
                      xs12
                      sm8
                    >
                      <v-layout
                        align-start
                        :wrap="$vuetify.breakpoint.mdAndDown"
                      >
                        <div
                          style="min-width: 210px; display: flex"
                          :style="{
                            'justify-content': $vuetify.breakpoint.xs
                              ? 'flex-start'
                              : 'flex-end',
                          }"
                        >
                          <allowed-menu-categories-select
                            class="mr-3"
                            :class="{'my-1': $vuetify.breakpoint.xs}"
                            :tvdecoder="tvdecoder"
                            :menu-id="menuId"
                            :label="$t('menuCategories')"
                            :warning="
                              tvdecoder.allowed_menu_categories.length == 0
                            "
                            hide-categories-link
                          />
                        </div>
                        <!-- Allowed menu categories chips for quick inspection/removal -->
                        <transition-group name="chips-transition">
                          <v-chip
                            v-for="menuCategory in tvdecoder.allowed_menu_categories"
                            :key="'allowed-menu-category-' + menuCategory.id"
                            label
                            close
                            class="
                              body-1
                              grey
                              lighten-3
                              outlined--dark
                              unselectable
                              category-chip
                            "
                            @input="
                              removeMenuCategoryFromTVDecoder(
                                menuCategory,
                                tvdecoder
                              )
                            "
                          >
                            {{ menuCategory.name }}
                          </v-chip>
                        </transition-group>
                      </v-layout>
                    </v-flex>
                  </v-layout>
                </transition-group>
              </div>
            </div>
          </transition-group>
        </div>
      </v-slide-x-transition>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
  </div>
</template>

<script>
import {mapState} from 'vuex';
import orderBy from 'lodash.orderby';

import {removeUrlQueryParamsFromCurrentRoute} from '@/utils';
import matchesFilter from '@/matchesFilter';

import {getMenuCategoryDecodersAssignmentRoute} from '@/components/Menu/MenuPagesNavbar';
import {
  isMenuCategoryAddedToTVDecoder,
  removeMenuCategoryFromTVDecoder,
} from '@/mixins/allowedMenuCategories.js';

import AllowedMenuCategoriesSelect from '@/components/AllowedMenuCategoriesSelect';
import IconTvDecoder from '@/components/IconTvDecoder';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';

export default {
  components: {
    AllowedMenuCategoriesSelect,
    IconTvDecoder,
    MenuPagesNavbar,
  },
  data() {
    return {
      loading: false,
      searchText: '',
      onlyDecodersWithoutMenuCategories: false,

      // From backend.
      menu: null,
      storesWithTVDecoders: [],
    };
  },
  computed: {
    ...mapState({
      menuCategories: (state) => state.common.menuCategories,
    }),
    orderedMenuCategories() {
      return orderBy(this.menuCategories, ['order', 'name']);
    },
    menuCategoryDecodersAssignmentRoute() {
      return getMenuCategoryDecodersAssignmentRoute(
        this.$store.state.common.lastStoreWithMenuVisited,
        this.$store.state.common.storesWithTVDecodersForMenuCategories
      );
    },
    tvdecoders() {
      let tvdecoders = [];
      for (let store of this.storesWithTVDecoders) {
        tvdecoders = tvdecoders.concat(store.tvdecoders);
      }
      return tvdecoders;
    },
    hasDecodersWithoutAllowedMenuCategories() {
      return this.tvdecoders.some(
        (tvdecoder) => tvdecoder.allowed_menu_categories.length === 0
      );
    },
    menuCategoriesNotUsedOnAnyPlayersYet() {
      return this.orderedMenuCategories.filter((menuCategory) => {
        return !this.tvdecoders.some((tvdecoder) =>
          tvdecoder.allowed_menu_categories.some(
            (category) => category.id === menuCategory.id
          )
        );
      });
    },
    numTVDecodersForCurrentSearch() {
      let num = 0;
      for (const store of this.filteredStoresWithTVDecoders) {
        num += store.tvdecoders.length;
      }
      return num;
    },
    numTVDecodersOverall() {
      let num = 0;
      for (const store of this.storesWithTVDecoders) {
        num += store.tvdecoders.length;
      }
      return num;
    },
    searchHint() {
      if (!this.searchText) {
        return `${this.numTVDecodersOverall} ${this.$t('decoders')}`;
      }
      return `${this.numTVDecodersForCurrentSearch}/${
        this.numTVDecodersOverall
      } ${this.$t('decoders')}`;
    },
    filteredStoresWithTVDecoders() {
      const filtered = [];

      // We want search to allow showing all matching Decoders primarily
      // e.g. 'Display 1' should list all those. Store city/name matches
      // are less important than that, same for category names.
      for (let store of this.storesWithTVDecoders) {
        const tvdecodersMatchingCategoryFilter = store.tvdecoders.filter(
          (tvdecoder) => {
            return (
              !this.onlyDecodersWithoutMenuCategories ||
              tvdecoder.allowed_menu_categories.length === 0
            );
          }
        );

        const storeMatchesSearchText = this.identsMatchingSearch.includes(
          store.ident
        );
        if (
          storeMatchesSearchText &&
          tvdecodersMatchingCategoryFilter.length > 0
        ) {
          store = Object.assign({}, store);
          store.tvdecoders = tvdecodersMatchingCategoryFilter;
          filtered.push(store);
          continue;
        }

        const tvdecodersAlsoMatchingSearchText =
          tvdecodersMatchingCategoryFilter.filter((tvdecoder) => {
            return this.identsMatchingSearch.includes(tvdecoder.ident);
          });
        if (tvdecodersAlsoMatchingSearchText.length > 0) {
          store = Object.assign({}, store);
          store.tvdecoders = tvdecodersAlsoMatchingSearchText;
          filtered.push(store);
        }
      }
      return filtered;
    },
    identsMatchingSearch() {
      const idents = [];

      this.storesWithTVDecoders.forEach((store) => {
        const storeMatches =
          !this.searchText ||
          matchesFilter(store.name, this.searchText) ||
          matchesFilter(store.city, this.searchText);
        if (storeMatches) {
          idents.push(store.ident);
        }

        const matchingTVDecoders = store.tvdecoders.filter((tvdecoder) => {
          return (
            !this.searchText ||
            matchesFilter(tvdecoder.description, this.searchText) ||
            matchesFilter(tvdecoder.mac, this.searchText) ||
            matchesFilter(
              tvdecoder.allowed_menu_categories
                .map((category) => category.name)
                .join(' '),
              this.searchText
            )
          );
        });
        for (const tvdecoder of matchingTVDecoders) {
          idents.push(tvdecoder.ident);
        }
      });

      return idents;
    },
    menuCategoryIdToMenuCategory() {
      const idToObject = {};
      for (const menuCategory of this.menuCategories) {
        idToObject[menuCategory.id] = menuCategory;
      }
      return idToObject;
    },
    menuId() {
      return this.$route.params.menuId;
    },
  },
  watch: {
    /**
     * Reset filter if it makes no sense anymore.
     */
    hasDecodersWithoutAllowedMenuCategories: function (
      hasDecodersWithoutCategories
    ) {
      if (
        !hasDecodersWithoutCategories &&
        this.onlyDecodersWithoutMenuCategories
      ) {
        this.onlyDecodersWithoutMenuCategories = false;
      }
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    isMenuCategoryAddedToTVDecoder: isMenuCategoryAddedToTVDecoder,
    removeMenuCategoryFromTVDecoder: removeMenuCategoryFromTVDecoder,
    onGoToStorePageLinkClicked(store) {
      this.$store.commit('SET_UNSET_PRE_ROUTE_QUESTION_ON_NOT_CONFIRMED', true);
      this.$store.commit(
        'SET_PRE_ROUTE_QUESTION',
        this.$t('menuItemAssignStoresDialogGoToStoreConfirm', {
          store: store.name + ', ' + store.city,
        })
      );
      this.$router.push({
        name: 'storemenu',
        params: {storeId: store.id},
      });
    },
    clearSearch() {
      this.searchText = '';
    },
    fetchData() {
      this.fetchMenu();
      this.fetchDecoders();
      this.$store.dispatch('LIST_MENU_CATEGORIES', this.menuId);
    },
    goToDecoderDetailPage(tvdecoder) {
      this.$router.push({
        name: 'decoder',
        params: {
          ctype: tvdecoder.ctype,
          decoderId: tvdecoder.id,
        },
      });
    },
    fetchMenu() {
      this.$api.retrieveMenu(this.menuId).then((response) => {
        this.menu = response.data;
      });
    },
    fetchDecoders() {
      this.loading = true;
      this.$api
        .listStoresWithTVDecodersForMenuCategories()
        .then((response) => {
          this.storesWithTVDecoders = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (
        vm.$route.query &&
        (vm.$route.query.search || vm.$route.query.without_categories)
      ) {
        vm.searchText = vm.$route.query.search;
        vm.onlyDecodersWithoutMenuCategories =
          !!vm.$route.query.without_categories;
        removeUrlQueryParamsFromCurrentRoute();
      }
    });
  },
};
</script>

<style scoped>
.v-input--checkbox.msr--text >>> .v-label {
  color: var(--msr-color-primary);
}
.v-input--checkbox.warning--text >>> .v-label {
  color: #fb8c00;
}
.decoder-row:hover {
  background-color: rgba(0, 0, 0, 0.06);
}
.decoder-row:hover .category-chip {
  background-color: rgba(0, 0, 0, 0.12) !important;
}

.chips-transition-enter-active,
.chips-transition-leave-active {
  transition: all 0.3s ease-in-out;
}
.chips-transition-enter,
.chips-transition-leave-to {
  opacity: 0;
  transform: scale(0);
}

.store-menu-anchor {
  text-decoration: none;
  cursor: pointer;
}
.store-menu-anchor:hover {
  text-decoration: underline;
}

.store-menu-anchor--row {
  background: transparent;
}
.store-menu-anchor--row:hover {
  background: rgba(0, 0, 0, 0.06);
}
.store-menu-anchor--row .store-menu-link--hint {
  display: none;
}
.store-menu-anchor--row:hover .store-menu-link--hint {
  display: flex;
  align-items: center;
}

.store-menu-anchor--row .store-menu-anchor {
  text-decoration: none;
  cursor: pointer;
}
.store-menu-anchor--row:hover .store-menu-anchor {
  text-decoration: underline;
}

.stores-transition-enter-active,
.stores-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.stores-transition-enter,
.stores-transition-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuCategoryDecoders.vue